// Zurich embed: the child-side transport. Embed detection and partner
// identification live in ./embed; Retail Trust's handshake in ./partnerEmbed.
//
// When up-spa runs inside Zurich's page, it drives the handshake itself: it publishes a PKCE challenge, receives a one-time
// authorization code, redeems it for a scoped token, and signs in with that.
//
// It used to talk to a broker (iframe-host) over an internal `app:*` contract,
// because the code had to be redeemed server-side. PKCE removed that
// requirement, so there is one frame and one contract now. See
// @/contracts/zurichEmbed.
//
// Security-critical validation (origin matching, schema parsing) is kept in
// pure, exported functions so it can be unit-tested in isolation.

import type {
  ParentInit,
  ParentRenew,
  ParentLogout,
  ParentTheme,
  EmbedInbound,
  EmbedOutbound,
  EmbedErrorCode,
  EmbedTheme,
} from "@/contracts/zurichEmbed";
import { createPkcePair } from "./pkce";
import { exchangeCode } from "./exchange";
import type { ScopedToken } from "./exchange";
import { createRenewal } from "./renewal";
import type { Renewal } from "./renewal";
import { startHeartbeat } from "./heartbeat";

// Parent (Zurich) origins allowed to drive the handshake. Comma-separated env
// value; empty ⇒ allow any parent (local dev only — deployed builds MUST set it).
const allowedHostOrigins = (process.env.VUE_APP_EMBED_HOST_ORIGINS ?? "")
  .split(",")
  .map((origin) => origin.trim())
  .filter(Boolean);

// A pattern may be an exact origin ("https://host.spectrum.life") or a wildcard
// subdomain ("https://*.spectrum.life", matching any subdomain and the apex).
export const originMatches = (pattern: string, origin: string): boolean => {
  if (pattern === origin) return true;
  const wildcard = pattern.match(/^(https?:\/\/)\*\.(.+)$/i);
  if (!wildcard) return false;
  const [, scheme, domain] = wildcard;
  return (
    origin.startsWith(scheme) && (origin.endsWith(`.${domain}`) || origin === `${scheme}${domain}`)
  );
};

export const isAllowedHostOrigin = (origin: string): boolean =>
  allowedHostOrigins.length === 0 ||
  allowedHostOrigins.some((pattern) => originMatches(pattern, origin));

const isEmbedTheme = (value: unknown): value is EmbedTheme => value === "light" || value === "dark";

// Zurich's schema sends an object; the bare string is the earlier form, still
// accepted. undefined means invalid, null means valid but carrying no mode.
const parseTheme = (theme: unknown): EmbedTheme | null | undefined => {
  if (isEmbedTheme(theme)) return theme;
  if (!theme || typeof theme !== "object") return undefined;
  const { mode } = theme as Record<string, unknown>;
  if (mode === undefined) return null;
  return isEmbedTheme(mode) ? mode : undefined;
};

// Manual runtime validation (up-spa has no schema library) — mirrors the
// defensive typeof checks the existing webchat consumer uses.
export const parseInboundMessage = (data: unknown): EmbedInbound | null => {
  if (!data || typeof data !== "object") return null;
  const message = data as Record<string, unknown>;
  if (message.v !== 1) return null;
  if (message.type === "parent:init") {
    if (typeof message.authorizationCode !== "string" || message.authorizationCode.length === 0) {
      return null;
    }
    return { type: "parent:init", v: 1, authorizationCode: message.authorizationCode };
  }
  if (message.type === "parent:renew") {
    if (
      typeof message.requestId !== "string" ||
      message.requestId.length === 0 ||
      typeof message.authorizationCode !== "string" ||
      message.authorizationCode.length === 0
    ) {
      return null;
    }
    return {
      type: "parent:renew",
      v: 1,
      requestId: message.requestId,
      authorizationCode: message.authorizationCode,
    };
  }
  if (message.type === "parent:logout") return { type: "parent:logout", v: 1 };
  if (message.type === "parent:theme") {
    const theme = parseTheme(message.theme);
    if (theme === undefined) return null;
    return { type: "parent:theme", v: 1, theme };
  }
  return null;
};

// A cross-origin frame cannot read its embedder's origin, so address each origin
// this build is configured to trust and let the browser deliver only to the one
// that matches. "*" is used only when the allowlist is empty, which is local dev.
//
// This matters more than it did when the only outbound messages were lifecycle
// signals: sl:ready now carries the code challenge. The challenge is not a
// secret, but broadcasting it invites a hostile embedder to obtain a code bound
// to it and feed that code back.
const post = (message: EmbedOutbound | Record<string, unknown>): void => {
  if (allowedHostOrigins.length === 0) {
    window.parent.postMessage(message, "*");
    return;
  }
  for (const origin of allowedHostOrigins) window.parent.postMessage(message, origin);
};

export const emitReady = (codeChallenge: string): void =>
  post({ type: "sl:ready", v: 1, codeChallenge, codeChallengeMethod: "S256" });
export const emitRenew = (requestId: string, codeChallenge: string): void =>
  post({ type: "sl:renew", v: 1, requestId, codeChallenge, codeChallengeMethod: "S256" });
export const emitError = (reason: EmbedErrorCode): void => post({ type: "sl:error", v: 1, reason });
export const emitClosing = (): void => post({ type: "sl:closing", v: 1 });

// Partner-level events that are not part of the handshake lifecycle (sl:close,
// sl:booking-complete) — the same messages Retail Trust receives, addressed to
// Zurich's allowlisted origins.
export const postToHost = (message: Record<string, unknown>): void => post(message);

let lastReportedHeight = -1;
export const emitResize = (height: number): void => {
  if (height === lastReportedHeight) return; // avoid spamming the parent with no-op changes
  lastReportedHeight = height;
  post({ type: "sl:resize", v: 1, height });
};

export type EmbedHandlers = {
  onInit: (message: ParentInit) => void;
  onRenew?: (message: ParentRenew) => void;
  onLogout?: (message: ParentLogout) => void;
  onTheme?: (message: ParentTheme) => void;
};

export const listenToHost = (handlers: EmbedHandlers): (() => void) => {
  const listener = (event: MessageEvent): void => {
    if (!isAllowedHostOrigin(event.origin)) return;
    if (event.source !== window.parent) return;
    const message = parseInboundMessage(event.data);
    if (!message) return;
    if (message.type === "parent:init") handlers.onInit(message);
    else if (message.type === "parent:renew") handlers.onRenew?.(message);
    else if (message.type === "parent:logout") handlers.onLogout?.(message);
    else handlers.onTheme?.(message);
  };
  window.addEventListener("message", listener);
  return () => window.removeEventListener("message", listener);
};

// Report document height to the parent so it can size our frame. Emits on
// change only. Returns a teardown.
export const startHeightReporting = (): (() => void) => {
  const report = (): void => emitResize(Math.ceil(document.documentElement.scrollHeight));
  const observer = new ResizeObserver(report);
  observer.observe(document.documentElement);
  report();
  return () => observer.disconnect();
};

// Minimal structural types for the Vuex store / router — avoids importing (and
// coupling to) their full generic types just to call two methods.
type EmbedStore = {
  dispatch: (type: string, payload?: unknown) => Promise<unknown>;
  commit: (type: string, payload?: unknown) => void;
  state: { user: { authenticated: boolean } };
};
type EmbedRouter = { push: (location: string) => Promise<unknown> };

// Fail the handshake if it has not completed in this window — the parent never
// sends init, the exchange hangs past its own timeout, and so on.
const HANDSHAKE_TIMEOUT_MS = 20000;

// Wire the transport into the app: generate PKCE, announce readiness with the
// challenge, redeem the code on parent:init, sign in, and report height; then
// keep the session alive — renew ahead of expiry (sl:renew → parent:renew)
// and poll the heartbeat for a session the server wants renewed sooner. Call
// once at startup when embedPartner() is ZURICH.
//
// Async only because the challenge must exist before anything is announced.
export const initEmbedChild = async (store: EmbedStore, router: EmbedRouter): Promise<void> => {
  const apiBaseUrl = process.env.VUE_APP_API_PATH ?? "";
  const clientId = process.env.VUE_APP_OAUTH_CLIENT_ID ?? "";

  let stopHeightReporting: (() => void) | null = null;
  let stopHeartbeat: (() => void) | null = null;
  let renewal: Renewal | null = null;
  // The token in use. Read by the heartbeat at each poll, replaced on renewal.
  let currentToken = "";
  let settled = false;

  // Held from boot so the sign-in screen never flashes while the handshake runs.
  // LoggedIn skips its /login redirect while this is set, so a failure has to
  // make that redirect itself — a parent that never answers still ends up on
  // the ordinary sign-in screen, just not behind a permanent spinner.
  store.commit("setEmbedAuthPending", true);
  const releaseLoader = (): void => {
    store.commit("setEmbedAuthPending", false);
    if (!store.state.user.authenticated) router.push("/login").catch(() => {});
  };

  const redeem = (code: string, verifier: string): Promise<ScopedToken> =>
    exchangeCode(apiBaseUrl, clientId, code, verifier);

  // Starts once, after the first successful sign-in. Renewal is the handshake
  // again with a fresh PKCE pair, in place: the frame is never reloaded, so the
  // member keeps their place in the app.
  const keepAlive = (first: ScopedToken): void => {
    currentToken = first.token;
    renewal = createRenewal({
      requestCode: emitRenew,
      redeem,
      onRenewed: (token) => {
        currentToken = token.token;
        store.commit("setEmbedToken", token.token);
      },
      // The session ran and has now ended without a renewal getting through.
      // State is kept: the parent may re-drive the handshake from sl:ready.
      onExhausted: () => emitError("session_expired"),
    });
    renewal.schedule(first);
    stopHeartbeat = startHeartbeat({
      apiBaseUrl,
      token: () => currentToken,
      onRenewRequired: () => renewal?.request(),
      // Re-arm the fallback timer on the server's clock.
      onExpiresIn: (seconds) => renewal?.schedule({ token: currentToken, expiresIn: seconds }),
    });
  };

  const stopKeepAlive = (): void => {
    renewal?.stop();
    renewal = null;
    stopHeartbeat?.();
    stopHeartbeat = null;
  };

  const pkce = await createPkcePair();

  const timer = setTimeout(() => {
    if (settled) return;
    emitError("init_failed");
    releaseLoader();
  }, HANDSHAKE_TIMEOUT_MS);

  // Registered before readiness is announced. The other order loses the reply
  // when the parent is quick, and the failure looks like a parent that never
  // answered.
  listenToHost({
    onInit: async ({ authorizationCode }) => {
      // The ready challenge is single-use: once a session exists, a further
      // parent:init can only carry a code bound to a verifier already spent.
      if (settled) return;
      let scoped: ScopedToken;
      try {
        scoped = await exchangeCode(apiBaseUrl, clientId, authorizationCode, pkce.verifier);
      } catch (e) {
        // Distinguished from auth_failed on purpose: this is the code exchange,
        // not the sign-in, and they fail for entirely different reasons.
        console.error("[embed] token exchange failed", e);
        emitError("exchange_failed");
        releaseLoader();
        return;
      }

      try {
        const authenticated = await store.dispatch("embedSignIn", { token: scoped.token, router });
        if (!authenticated) {
          emitError("auth_failed");
          releaseLoader();
          return;
        }
        settled = true;
        clearTimeout(timer);
        releaseLoader();
        if (!stopHeightReporting) stopHeightReporting = startHeightReporting();
        keepAlive(scoped);
      } catch (e) {
        console.error("[embed] sign-in threw", e);
        emitError("auth_failed");
        releaseLoader();
      }
    },
    onRenew: ({ requestId, authorizationCode }) => {
      renewal?.accept(requestId, authorizationCode);
    },
    onLogout: () => {
      stopKeepAlive();
      store.dispatch("logout");
      stopHeightReporting?.();
      stopHeightReporting = null;
    },
  });

  window.addEventListener("pagehide", () => {
    stopKeepAlive();
    emitClosing();
  });

  emitReady(pkce.challenge);
};
